<template>
  <div class="etp">
    <p class="etp-prompt">Choose which entry of each selected listing the update is written to.</p>
    <ul class="etp-options">
      <li v-for="opt in options" :key="opt.value">
        <label class="etp-opt">
          <input type="radio" name="etp-target" :value="opt.value" :checked="target === opt.value"
            @change="$emit('update:target', opt.value)" />
          <span class="etp-opt-text">
            <span class="etp-opt-label">
              {{ opt.label }}
              <input v-if="opt.value === 'nth'" type="number" min="1" step="1" class="etp-nth"
                :value="entryNumber" aria-label="Entry number to update"
                @input="onNumberInput" @focus="$emit('update:target', 'nth')" @click.stop />
            </span>
            <span class="etp-opt-hint">{{ opt.hint }}</span>
          </span>
        </label>
      </li>
    </ul>
    <ul class="etp-list">
      <li v-for="r in preview" :key="'etp-' + r.cid" class="etp-item">
        <span class="etp-item-label">{{ r.label }}</span>
        <span class="etp-item-cid" :class="{ 'etp-item-cid--warn': r.warn }">{{ r.text }} · #{{ r.cid }}</span>
      </li>
    </ul>
    <p v-if="listings.length > preview.length" class="etp-more">+ {{ listings.length - preview.length }} more</p>
  </div>
</template>

<script>
// Entry picker for Bulk Update. Pure presentation: the parent owns `target`
// ('original' | 'current' | 'nth' | 'new') and `entryNumber` via .sync and decides
// what to post. `listings` is [{ cid, unit_number, entryCount }].
export default {
  name: 'EntryTargetPicker',
  props: {
    target: { type: String, required: true },
    entryNumber: { type: [Number, String], default: 2 },
    listings: { type: Array, required: true },
  },
  data() {
    return {
      options: [
        { value: 'original', label: 'Original entry', hint: 'The first entry on file for each listing.' },
        { value: 'current', label: 'Current entry', hint: 'The latest entry for each listing.' },
        { value: 'nth', label: 'Entry number', hint: 'Count from the oldest: 1 is the original, 2 is the second entry, and so on.' },
        { value: 'new', label: 'New entry', hint: 'Adds a new entry to each listing instead of changing an existing one.' },
      ],
    };
  },
  computed: {
    preview() {
      return this.listings.slice(0, 8).map(l => ({ cid: l.cid, label: l.unit_number, ...this.describe(l) }));
    },
  },
  methods: {
    onNumberInput(e) {
      this.$emit('update:entryNumber', e.target.value === '' ? '' : Math.floor(Number(e.target.value)));
      this.$emit('update:target', 'nth');
    },
    describe(l) {
      // Other pages open Bulk Update without per-listing entry counts, so
      // describe the target in words and leave the check to the server.
      if (l.entryCount == null) {
        const words = { new: 'Adds a new entry', original: 'Original entry', current: 'Current entry' };
        return { text: this.target === 'nth' ? `Entry ${Math.floor(Number(this.entryNumber)) || '?'}` : words[this.target], warn: false };
      }
      const total = Number(l.entryCount) || 0;
      if (this.target === 'new') return { text: `Adds entry ${total + 1}`, warn: false };
      if (!total) return { text: 'No entries found', warn: true };
      let position = total;
      if (this.target === 'original') position = 1;
      else if (this.target === 'nth') position = Math.floor(Number(this.entryNumber)) || 0;
      if (position < 1 || position > total) {
        return { text: `No entry ${position || '?'} (has ${total}) — skipped`, warn: true };
      }
      return { text: `Entry ${position} of ${total}`, warn: false };
    },
  },
};
</script>

<style scoped>
.etp-prompt {
  margin: 0 0 12px;
  font-size: 14px;
  color: var(--rpd-muted, #5C6B80);
}

.etp-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.etp-opt {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin: 0;
  padding: 14px 16px;
  border: 1px solid var(--rpd-line, #E3E8EF);
  border-radius: 8px;
  background: var(--rpd-white, #ffffff);
  cursor: pointer;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1), border-color 120ms cubic-bezier(0.2, 0, 0, 1);
}

.etp-opt:hover {
  border-color: #C9CCD2;
}

.etp-opt:has(input[type="radio"]:checked) {
  background: var(--rpd-bg-2, #F4F6F9);
  border-color: var(--rpd-navy-800, #15273D);
}

.etp-opt input[type="radio"] {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: var(--rpd-navy-800, #15273D);
  cursor: pointer;
}

.etp-opt-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.etp-opt-label {
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
  color: var(--rpd-ink, #0A1320);
}

.etp-opt-hint {
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: var(--rpd-muted, #5C6B80);
}

.etp-nth {
  width: 64px;
  margin-left: 8px;
  padding: 2px 6px;
  font-size: 14px;
  border: 1px solid var(--rpd-line, #E3E8EF);
  border-radius: 4px;
  background: var(--rpd-white, #ffffff);
  color: var(--rpd-ink, #0A1320);
}

.etp-list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  border: 1px solid var(--rpd-line, #E3E8EF);
  border-radius: 8px;
}

.etp-item {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;
  padding: 8px 12px;
  font-size: 13px;
  border-bottom: 1px solid var(--rpd-line-2, #EFF2F6);
}

.etp-item:last-child {
  border-bottom: none;
}

.etp-item-cid {
  font-size: 11px;
  color: #8A99A8;
  font-variant-numeric: tabular-nums;
}

.etp-item-cid--warn {
  color: var(--rpd-coral-dark, #D94B52);
  font-weight: 600;
}

.etp-more {
  margin: 8px 0 0;
  font-size: 12px;
  color: #8A99A8;
}

html.dark .etp-opt {
  background: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.1);
}

html.dark .etp-opt:hover {
  border-color: rgba(255, 255, 255, 0.22);
}

html.dark .etp-opt:has(input[type="radio"]:checked) {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--rpd-coral, #F26A6F);
}

html.dark .etp-opt input[type="radio"] {
  accent-color: var(--rpd-coral, #F26A6F);
}

html.dark .etp-opt-label,
html.dark .etp-item {
  color: #E3E8EF;
}

html.dark .etp-opt-hint {
  color: #8AA0B8;
}

html.dark .etp-nth {
  background: transparent;
  color: #E3E8EF;
  border-color: rgba(255, 255, 255, 0.2);
}

html.dark .etp-list {
  border-color: rgba(255, 255, 255, 0.12);
}

html.dark .etp-item {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
</style>
